import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Paper,
  Stack,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  TrendingUp as IncomeIcon,
  TrendingDown as ExpenseIcon,
  AccountBalance as BalanceIcon,
} from '@mui/icons-material';
import { format, addMonths, subMonths } from 'date-fns';
import { useBudget } from '../context/BudgetContext';

const MonthlySummary: React.FC = () => {
  const { monthlySummary, currentMonth, setCurrentMonth } = useBudget();

  const handlePreviousMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const handleCurrentMonth = () => {
    setCurrentMonth(new Date());
  };

  const isCurrentMonth = 
    currentMonth.getMonth() === new Date().getMonth() &&
    currentMonth.getFullYear() === new Date().getFullYear();

  return (
    <Paper elevation={0} sx={{ mb: 3, overflow: 'hidden' }}>
      <Box sx={{ 
        p: 3, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        }
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} position="relative" zIndex={1}>
          <IconButton
            onClick={handlePreviousMonth}
            sx={{ 
              color: 'inherit',
              bgcolor: 'rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
            }}
            size="small"
          >
            <ChevronLeftIcon />
          </IconButton>

          <Box textAlign="center" onClick={handleCurrentMonth} sx={{ cursor: 'pointer' }}>
            <Typography variant="h5" fontWeight={700} letterSpacing={0.5}>
              {format(currentMonth, 'MMMM yyyy')}
            </Typography>
            {!isCurrentMonth && (
              <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
                Tap to go to current month
              </Typography>
            )}
          </Box>

          <IconButton
            onClick={handleNextMonth}
            sx={{ 
              color: 'inherit',
              bgcolor: 'rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
            }}
            size="small"
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>

        {/* Summary Cards */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} position="relative" zIndex={1}>
          {/* Total Income */}
          <Box flex={1}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.95) 0%, rgba(56, 142, 60, 0.95) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
              boxShadow: '0 8px 32px 0 rgba(76, 175, 80, 0.37)',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px 0 rgba(76, 175, 80, 0.5)',
              }
            }}>
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                  <Box sx={{ 
                    bgcolor: 'rgba(255,255,255,0.25)', 
                    borderRadius: '50%', 
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <IncomeIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, fontSize: '0.95rem' }}>
                    Income
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={800} sx={{ color: 'white', letterSpacing: '-0.5px' }}>
                  £{monthlySummary.totalIncome.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Total Expenses */}
          <Box flex={1}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.95) 0%, rgba(211, 47, 47, 0.95) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
              boxShadow: '0 8px 32px 0 rgba(244, 67, 54, 0.37)',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px 0 rgba(244, 67, 54, 0.5)',
              }
            }}>
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                  <Box sx={{ 
                    bgcolor: 'rgba(255,255,255,0.25)', 
                    borderRadius: '50%', 
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <ExpenseIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, fontSize: '0.95rem' }}>
                    Expenses
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={800} sx={{ color: 'white', letterSpacing: '-0.5px' }}>
                  £{monthlySummary.totalExpenses.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Balance */}
          <Box flex={1}>
            <Card sx={{ 
              background: monthlySummary.balance >= 0
                ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.95) 0%, rgba(25, 118, 210, 0.95) 100%)'
                : 'linear-gradient(135deg, rgba(255, 152, 0, 0.95) 0%, rgba(245, 124, 0, 0.95) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
              boxShadow: monthlySummary.balance >= 0
                ? '0 8px 32px 0 rgba(33, 150, 243, 0.37)'
                : '0 8px 32px 0 rgba(255, 152, 0, 0.37)',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: monthlySummary.balance >= 0
                  ? '0 12px 40px 0 rgba(33, 150, 243, 0.5)'
                  : '0 12px 40px 0 rgba(255, 152, 0, 0.5)',
              }
            }}>
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                  <Box sx={{ 
                    bgcolor: 'rgba(255,255,255,0.25)', 
                    borderRadius: '50%', 
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <BalanceIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, fontSize: '0.95rem' }}>
                    Balance
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  fontWeight={800}
                  sx={{
                    color: 'white',
                    letterSpacing: '-0.5px',
                  }}
                >
                  {monthlySummary.balance >= 0 ? '+' : ''}£{monthlySummary.balance.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Stack>

        {/* Entry Count */}
        <Box mt={3} textAlign="center" position="relative" zIndex={1}>
          <Typography variant="body2" sx={{ opacity: 0.95, fontWeight: 500 }}>
            {monthlySummary.entryCount} {monthlySummary.entryCount === 1 ? 'entry' : 'entries'} this month
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default MonthlySummary;
